import React, { useMemo } from 'react';
import OptimizedChart from '../utils/OptimizedChart';
import { useApexTheme } from '../utils/apexTheme';

const DrawdownChart = ({ equitySeries, height = 200 }) => {
  const { colors } = useApexTheme();

  const { componentOptions, series } = useMemo(() => {
    if (!equitySeries || equitySeries.length === 0) return { componentOptions: null, series: [] };

    // Calculate drawdown from equity series
    let peak = equitySeries[0]?.equity || 0;
    const drawdownData = equitySeries.map(item => {
      if (item.equity > peak) {
        peak = item.equity;
      }
      const drawdown = peak > 0 ? ((item.equity - peak) / peak) * 100 : 0;
      return {
        x: new Date(item.timestamp).getTime(),
        y: drawdown
      };
    });

    // Find max drawdown
    const maxDrawdown = Math.min(...drawdownData.map(d => d.y));

    const options = {
      chart: {
        type: 'area',
        height,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: true
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      colors: [colors.error || '#f44336'],
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeFormatter: {
            year: 'yyyy',
            month: "MMM 'yy",
            day: 'dd MMM'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Drawdown (%)'
        },
        labels: {
          formatter: (value) => value.toFixed(1) + '%'
        },
        max: 0,
        min: maxDrawdown * 1.1
      },
      tooltip: {
        x: {
          format: 'dd MMM yyyy'
        },
        y: {
          formatter: (value) => value.toFixed(2) + '%'
        }
      },
      annotations: {
        yaxis: [{
          y: maxDrawdown,
          borderColor: colors.error || '#d32f2f',
          label: {
            borderColor: colors.error || '#d32f2f',
            style: {
              color: '#fff',
              background: colors.error || '#d32f2f'
            },
            text: `Max DD: ${maxDrawdown.toFixed(2)}%`
          }
        }]
      }
    };

    return {
      componentOptions: options,
      series: [{
        name: 'Drawdown',
        data: drawdownData
      }]
    };
  }, [equitySeries, height, colors]);

  if (!componentOptions) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No equity data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Drawdown</h4>
      <OptimizedChart
        componentOptions={componentOptions}
        series={series}
        type="area"
        height={height}
      />
    </div>
  );
};

export default DrawdownChart;
